class AdminApiService {
  private baseUrl: string;
  private token: string | null = null;

  constructor() {
    // Auto-detect the correct API URL based on environment
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (isDevelopment) {
      // Development: use localhost
      this.baseUrl = import.meta.env.VITE_ADMIN_API_URL || 'http://localhost:3001/api/admin';
    } else {
      // Production: use current domain
      this.baseUrl = `${window.location.origin}/api/admin`;
    }

    console.log('🌐 Admin API URL:', this.baseUrl);

    // Load token from localStorage on initialization
    this.token = localStorage.getItem('admin_api_token');
  }

  private getAuthHeaders() {
    // Make sure we have the latest token
    if (!this.token) {
      this.token = localStorage.getItem('admin_api_token');
    }
    
    return {
      'Content-Type': 'application/json',
      ...(this.token && { Authorization: `Bearer ${this.token}` })
    };
  }

  async login(password: string): Promise<{ success: boolean; token?: string; error?: string }> {
    try {
      console.log('🔐 Attempting login...');
      
      const response = await fetch(`${this.baseUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Login successful, storing token');
        this.token = data.token;
        localStorage.setItem('admin_api_token', data.token);
        return { success: true, token: data.token };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async validateToken(): Promise<boolean> {
    const storedToken = localStorage.getItem('admin_api_token');
    if (!storedToken) {
      //console.log('❌ No token in localStorage');
      return false;
    }

    try {
      console.log('🔍 Validating token...');
      
      const response = await fetch(`${this.baseUrl}/auth/validate`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });

      const data = await response.json();
      
      if (data.valid) {
        console.log('✅ Token is valid');
        this.token = storedToken;
        return true;
      } else {
        console.log('❌ Token is invalid');
        localStorage.removeItem('admin_api_token');
        this.token = null;
        return false;
      }
    } catch (error) {
      console.error('Token validation error:', error);
      localStorage.removeItem('admin_api_token');
      this.token = null;
      return false;
    }
  }

  async getUsers(): Promise<{ success: boolean; users?: any[]; error?: string }> {
    try {
      console.log('👥 Fetching users...');
      console.log('🔑 Using token:', this.token ? 'Present' : 'Missing');
      
      const headers = this.getAuthHeaders();
      console.log('📋 Request headers:', headers);
      
      const response = await fetch(`${this.baseUrl}/users`, {
        headers
      });

      const data = await response.json();
      console.log('📄 Response:', data);

      if (response.ok && data.success) {
        return { success: true, users: data.users };
      } else {
        return { success: false, error: data.error || 'Failed to fetch users' };
      }
    } catch (error) {
      console.error('Get users error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async enableUser(username: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/users/${username}/enable`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();
      return { success: data.success, error: data.error };
    } catch (error) {
      console.error('Enable user error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async disableUser(username: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/users/${username}/disable`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();
      return { success: data.success, error: data.error };
    } catch (error) {
      console.error('Disable user error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async deleteUser(username: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/users/${username}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();
      return { success: data.success, error: data.error };
    } catch (error) {
      console.error('Delete user error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  logout() {
    this.token = null;
    localStorage.removeItem('admin_api_token');
  }
}

export const adminApiService = new AdminApiService();